/**
 * FAQ primitive — a grouped question/answer accordion rendered from `faq` content blocks.
 *
 * This is an OPTIONAL content section: it is not part of any step's default layout. An author adds it
 * to a content step's `step_layout` (and authors `faq` content blocks) when they want a reference
 * Q&A panel; a step with no `faq` blocks renders nothing (no bundled/fallback copy).
 *
 * Each `faq` content block is one Q/A pair: `payload.q` / `payload.a`, with an optional `payload.group`
 * (or the block `label`) used to bucket pairs under a heading. Blocks render in their seeded `order`.
 */
import { Fragment, useMemo } from 'react';
import { resolveContentBlocks } from '../../api';
import { Block, ui } from '../../components/ui/primitives';
import { asIconName } from '../../components/ui/Icon';
import type { PrimitiveProps } from './registry';

interface FaqEntry {
  group: string;
  q: string;
  a: string;
}

export function FaqBlock({ config }: PrimitiveProps): JSX.Element | null {
  const entries = useMemo<FaqEntry[]>(() => {
    return [...resolveContentBlocks('faq')]
      .sort((a, b) => a.order - b.order)
      .map((b) => {
        const q = typeof b.payload?.q === 'string' ? b.payload.q.trim() : '';
        const a = typeof b.payload?.a === 'string' ? b.payload.a.trim() : '';
        const group =
          (typeof b.payload?.group === 'string' && b.payload.group.trim()) ||
          (b.label && b.label.trim()) ||
          '';
        return { group, q, a };
      })
      .filter((e) => e.q && e.a);
  }, []);

  // No authored FAQ content → render nothing (this is a purely optional, DB-driven section).
  if (entries.length === 0) return null;

  // Preserve first-seen group order.
  const groups: { group: string; items: FaqEntry[] }[] = [];
  for (const e of entries) {
    let bucket = groups.find((g) => g.group === e.group);
    if (!bucket) {
      bucket = { group: e.group, items: [] };
      groups.push(bucket);
    }
    bucket.items.push(e);
  }

  return (
    <Block
      icon={asIconName(config?.icon, 'chat')}
      title={(config?.title as string) || 'Frequently asked questions'}
      lead={(config?.lead as string) || undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {groups.map((g, gi) => (
          <Fragment key={g.group || `group-${gi}`}>
            {g.group && (
              <p style={{ margin: '4px 0 0', fontWeight: 700, fontSize: '0.8125rem' }}>{g.group}</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {g.items.map((item, i) => (
                <details
                  key={`${g.group}-${i}`}
                  style={{
                    border: '1px solid var(--border, rgba(0,0,0,0.12))',
                    borderRadius: 8,
                    padding: '10px 14px',
                  }}
                >
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{item.q}</summary>
                  <p className={ui.qaWhy} style={{ marginTop: 8 }}>
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </Fragment>
        ))}
      </div>
    </Block>
  );
}

export default FaqBlock;
